export interface ChatMetricTokenData {
  value?: number | string;
  label?: string;
  unit?: string;
  columns?: string[];
  rows?: (string | number)[][];
}

export interface ChatMetricToken {
  type: string;
  data?: ChatMetricTokenData;
}

const TOKEN_PLACEHOLDER_RE = /\[\[token:(\d+)\]\]/g;

export function resolveMessageTokens(content: string, tokens: ChatMetricToken[] = []): string {
  if (!content) {
    return '';
  }

  return content.replace(TOKEN_PLACEHOLDER_RE, (_, indexStr) => {
    const index = Number(indexStr);
    return formatTokenPlainText(tokens[index]);
  });
}

export function formatTokenPlainText(token: ChatMetricToken | undefined): string {
  if (!token) {
    return '';
  }

  if (token.type === 'metric' && token.data) {
    const { unit = '', value, label } = token.data;
    const valueText = value === undefined || value === null ? '' : String(value);
    const formatted = `${unit}${valueText}`;
    return label ? `${label}: ${formatted}` : formatted;
  }

  if (token.type === 'table' && token.data) {
    return formatTablePlainText(token.data);
  }

  return '';
}

function formatTablePlainText(data: ChatMetricTokenData): string {
  const columns = data.columns ?? [];
  const rows = data.rows ?? [];
  if (!columns.length) {
    return rows.map((row) => row.join(' | ')).join('\n');
  }

  const lines = [columns.join(' | ')];
  for (const row of rows) {
    lines.push(row.map((cell) => String(cell ?? '')).join(' | '));
  }
  return lines.join('\n');
}

export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const MD_INLINE_RE = /!\[([^\]]*)\]\(([^)]+)\)|\[([^\]]+)\]\(([^)]+)\)|\*\*(.+?)\*\*/g;

const CHAT_IMG_STYLE =
  'display:block;max-width:100%;height:auto;margin:8px 0;border-radius:8px;border:1px solid #e2e8f0;';

/** Shown when markdown image URL is invalid or fails to load. */
export const CHAT_IMAGE_FALLBACK_SRC = 'assets/noImage.PNG';

function isSafeHttpUrl(url: string): boolean {
  try {
    const parsed = new URL(url.trim());
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
}

function formatMarkdownImage(alt: string, url: string): string {
  const trimmedUrl = url.trim();
  const safeAlt = escapeHtml(alt);
  const fallback = CHAT_IMAGE_FALLBACK_SRC;
  const src = isSafeHttpUrl(trimmedUrl) ? escapeHtml(trimmedUrl) : fallback;
  const onerror = `this.onerror=null;this.src='${fallback}';`;
  return `<img class="d2-chat-md-img" style="${CHAT_IMG_STYLE}" src="${src}" alt="${safeAlt}" loading="eager" decoding="async" onerror="${onerror}" />`;
}

function formatMarkdownLink(label: string, url: string): string {
  const trimmedUrl = url.trim();
  if (!isSafeHttpUrl(trimmedUrl)) {
    return escapeHtml(`[${label}](${url})`);
  }
  const safeUrl = escapeHtml(trimmedUrl);
  return `<a class="d2-chat-md-link" href="${safeUrl}" target="_blank" rel="noopener noreferrer">${escapeHtml(label)}</a>`;
}

/**
 * AI replies often use markdown list markers before images, e.g.:
 * "5. Biriyani\n-\n![img](url)" or "- ![img](url)" — strip those stray hyphens.
 */
function normalizeChatMarkdown(content: string): string {
  return content
    .replace(/^\s*-\s+(?=!\[)/gm, '')
    .replace(/^\s*-\s*(?=\[\[token:\d+\]\])/gm, '')
    .replace(/^\s*-\s*$/gm, '')
    .replace(/\n{3,}/g, '\n\n');
}

function escapeAndFormatPlainText(text: string): string {
  let html = escapeHtml(text);
  html = html.replace(/^### (.+)$/gm, '<strong class="d2-chat-md-h3">$1</strong><br>');
  html = html.replace(/^#### (.+)$/gm, '<strong class="d2-chat-md-h4">$1</strong><br>');
  html = html.replace(/\n/g, '<br>');
  return html;
}

/** Bold, links, images, headers, and line breaks (plain text is escaped). */
function formatInlineTextHtml(text: string): string {
  const parts: string[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  const re = new RegExp(MD_INLINE_RE.source, 'g');
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(escapeAndFormatPlainText(text.slice(lastIndex, match.index)));
    }

    const token = match[0];
    if (token.startsWith('![')) {
      parts.push(formatMarkdownImage(match[1] ?? '', match[2] ?? ''));
    } else if (token.startsWith('[')) {
      parts.push(formatMarkdownLink(match[3] ?? '', match[4] ?? ''));
    } else {
      parts.push(`<strong>${escapeHtml(match[5] ?? '')}</strong>`);
    }

    lastIndex = match.index + token.length;
  }

  if (lastIndex < text.length) {
    parts.push(escapeAndFormatPlainText(text.slice(lastIndex)));
  }

  return parts.join('');
}

function formatTokenHtml(token: ChatMetricToken | undefined): string {
  if (!token) {
    return '';
  }

  if (token.type === 'table' && token.data) {
    return formatTableHtml(token.data);
  }

  if (token.type === 'metric' && token.data) {
    return formatInlineTextHtml(formatTokenPlainText(token));
  }

  return '';
}

/** Inline styles — required because [innerHTML] is outside Angular style encapsulation. */
const CHAT_TABLE_WRAP_STYLE =
  'display:block;width:100%;max-width:100%;margin-top:8px;box-sizing:border-box;border:1px solid #c5cdd8;border-radius:8px;overflow:hidden;';
const CHAT_TABLE_STYLE =
  'width:100%;max-width:100%;border-collapse:collapse;border:0;margin:0;table-layout:fixed;';
const CHAT_TABLE_CELL_BASE =
  'padding:8px 10px;text-align:left;vertical-align:top;border:0;border-bottom:1px solid #c5cdd8;border-right:1px solid #c5cdd8;word-break:break-word;';
const CHAT_TABLE_HEAD_CELL_EXTRA = 'background:#f8fafc;font-weight:600;color:#394356;';

function formatTableHtml(data: ChatMetricTokenData): string {
  const columns = data.columns ?? [];
  const rows = data.rows ?? [];
  if (!columns.length && !rows.length) {
    return '';
  }

  const colCount = Math.max(columns.length, ...rows.map((r) => r.length), 1);
  const isLastCol = (colIndex: number) => colIndex === colCount - 1;
  const cellStyle = (colIndex: number, isLastRow: boolean, isHead: boolean) => {
    let style = CHAT_TABLE_CELL_BASE;
    if (isHead) {
      style += CHAT_TABLE_HEAD_CELL_EXTRA;
    }
    if (isLastCol(colIndex)) {
      style += 'border-right:0;';
    }
    if (isLastRow) {
      style += 'border-bottom:0;';
    }
    return style;
  };

  const parts: string[] = [
    `<div class="d2-chat-table-wrap" style="${CHAT_TABLE_WRAP_STYLE}">`,
    `<table class="d2-chat-table" style="${CHAT_TABLE_STYLE}">`,
  ];

  if (columns.length) {
    parts.push('<thead><tr>');
    columns.forEach((col, colIndex) => {
      parts.push(`<th style="${cellStyle(colIndex, false, true)}">${escapeHtml(String(col))}</th>`);
    });
    parts.push('</tr></thead>');
  }

  parts.push('<tbody>');
  rows.forEach((row, rowIndex) => {
    const isLastRow = rowIndex === rows.length - 1;
    parts.push('<tr>');
    for (let colIndex = 0; colIndex < colCount; colIndex++) {
      const cell = row[colIndex];
      parts.push(
        `<td style="${cellStyle(colIndex, isLastRow, false)}">${escapeHtml(String(cell ?? ''))}</td>`,
      );
    }
    parts.push('</tr>');
  });
  parts.push('</tbody></table></div>');

  return parts.join('');
}

/** Escape text, apply markdown, resolve [[token:n]] (tables render as HTML). */
export function formatChatMessageHtml(content: string, tokens: ChatMetricToken[] = []): string {
  if (!content) {
    return '';
  }

  const normalized = normalizeChatMarkdown(content);
  const parts: string[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  const re = new RegExp(TOKEN_PLACEHOLDER_RE.source, 'g');
  while ((match = re.exec(normalized)) !== null) {
    const before = normalized.slice(lastIndex, match.index);
    if (before) {
      parts.push(formatInlineTextHtml(before));
    }
    parts.push(formatTokenHtml(tokens[Number(match[1])]));
    lastIndex = match.index + match[0].length;
  }

  const tail = normalized.slice(lastIndex);
  if (tail) {
    parts.push(formatInlineTextHtml(tail));
  }

  return parts.join('');
}

export function extractTokensFromPayload(data: unknown): ChatMetricToken[] {
  if (!data || typeof data !== 'object') {
    return [];
  }

  const record = data as Record<string, unknown>;
  const nested = record['data'] as Record<string, unknown> | undefined;
  const message =
    record['message'] && typeof record['message'] === 'object'
      ? (record['message'] as Record<string, unknown>)
      : undefined;

  const candidates = [record['tokens'], nested?.['tokens'], message?.['tokens']];

  for (const tokens of candidates) {
    if (Array.isArray(tokens)) {
      return tokens as ChatMetricToken[];
    }
  }

  return [];
}

export function normalizeApiTokens(raw: unknown): ChatMetricToken[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw as ChatMetricToken[];
}
